<template>
  <div class="relative mt-4 mb-6 lg:mt-0 lg:mb-0">

    <!-- flash message -->
    <div
      v-if="showInvalidSignatureError"
      class="!my-4 block md:hidden"
    >
      <adivare-alert
        type="danger"
        size="xs"
        show-icon
      >
        {{ $t('messages_nl.label.cancellation.userLetter.signature.mobileError') }}
      </adivare-alert>
    </div>

    <!-- box -->
    <div class="box-theme box-theme--shadow-against-white-background box-theme--border-against-white-background box-theme--primary relative w-full select-none z-10 after:content-[''] after:block after:absolute after:left-0 after:right-0 after:top-0 after:bottom-0 after:z-min-1 after:opacity-60 after:bg-paper-texture after:rounded-xl">

      <!-- modal: guaranteed -->
      <adivare-sweet-alert-modal
        v-if="isGuaranteed"
        :modal-title="$t('messages_nl.partials.guarantee.modal.title')"
        show-confirm-button
        id="guarantee"
      >
        <template v-slot:button>

          <!-- guaranteed logo -->
          <div class="absolute hidden md:block -right-3 -top-10 oscillate-105">
            <img
              v-if="isGuaranteed"
              :src="$asset('img/cancellation/guaranteed-blue-240x240.png')"
              :alt="$t('messages_nl.partials.guarantee.banner.altImage', { companyName: getCancellationProperty('companyName') })"
              :title="$t('messages_nl.partials.guarantee.banner.altImage', { companyName: getCancellationProperty('companyName') })"
              width="100"
              height="100"
              class="drop-shadow-lg cursor-pointer rotate-12 border-1 border-dark-grey rounded-full transition-transform hover:scale-110"
              loading="eager"
            >
          </div>

          <!-- guaranteed banner -->
          <div class="py-3 px-6 flex items-center w-full rounded-t-xl bg-white/60 h-14 top-0 left-0 right-0 px-6 border-1 border-box-against-white-background cursor-pointer">
            <p class="text-lg font-bold text-red w-full text-center md:text-left transition-transform origin-left hover:scale-105">{{ $t('messages_nl.partials.guarantee.banner.title') }}</p>
          </div>
        </template>
        <template v-slot:modalHtml>
          <adivare-guaranteed-modal :companyName="getCancellationProperty('companyName')"/>
        </template>
      </adivare-sweet-alert-modal>
      <adivare-collapse
        breakpoint="<md"
        :expanded="expandCollapsible"
      >
        <template v-slot:toggle="slotProps">
          <div class="overflow-hidden relative">
            <div class="flex items-center justify-center text-lg my-6">
              <p class="text-fontcolor-blue font-bold z-10">{{ $t('messages_nl.partials.letter.viewLetter') }}</p>
              <img
                :src="$asset('img/common/arrows/dropdown-arrow-to-bottom-blue.svg')"
                :alt="$t('messages_nl.images.arrow')"
                loading="eager"
                :class="['transition-all', {'rotate-180': slotProps.expanded}]"
                width="35"
                height="35"
              >
            </div>
          </div>
        </template>
        <template #collapsibleContent>

          <!-- letter -->
          <div class="relative block m-auto w-20">
            <img
              :src="$asset('img/common/logo/letter-logo-full.svg')"
              alt="logo"
              loading="eager"
              class="absolute pt-3 px-2 pb-2 rounded-b w-16 bg-2x-dark-blue"
            >
          </div>

          <div class="font-written text-sm p-6">
            <slot name="preview">
              <br />
              <br />
              <br />
              <br />

              <!-- COMPANY INFO: generic selected cancellation -->
              <template v-if="cancellationSelected && generic">

                <!-- company name -->
                <adivare-preview-element property="companyName" />
                <br />

                <!-- company address -->
                <adivare-preview-element property="companyAddress" />
                <br />

                <!-- company postcode & city -->
                <adivare-preview-element property="companyPostcode" /> <adivare-preview-element property="companyCity" />
              </template>

              <!-- COMPANY INFO: selected cancellation, but not generic-->
              <template v-else-if="cancellationSelected && !generic">

                <!-- company name -->
                <p>{{ getCancellationProperty('companyName') }}</p>

                <!-- company attn -->
                <template v-if="getCancellationProperty('companyAttn')">
                  <p>{{ getCancellationProperty('companyAttn') }}</p>
                </template>

                <!-- company address -->
                <p
                  class="popper-button"
                  ref="data-popper-button"
                  @focus="showPopper()"
                  @mouseover="showPopper()"
                  @mouseleave="hidePopper()"
                  @blur="hidePopper()"
                  aria-describedby="tooltip"
                >
                  {{ $t('messages_nl.label.companyAddress') }}
                </p>
                <span
                  ref="data-popper-tooltip"
                  class="hidden popper-tooltip"
                >
                  {{ $t('messages_nl.letter.content.blurTooltip') }}
                </span>

                <!-- company postcode & city -->
                <p>{{ getCancellationProperty('companyPostcode') + ' ' + getCancellationProperty('companyCity')}}</p>
              </template>

              <!-- COMPANY INFO: non selected cancellation-->
              <template v-else>

                <!-- company name -->
                <p>{{ $t('messages_nl.label.companyName') }}</p>

                <!-- company address -->
                <p
                  class="popper-button"
                  ref="data-popper-button"
                  @focus="showPopper()"
                  @mouseover="showPopper()"
                  @mouseleave="hidePopper()"
                  @blur="hidePopper()"
                  aria-describedby="tooltip"
                >
                  {{ $t('messages_nl.label.companyAddress') }}
                </p>
                <span
                  ref="data-popper-tooltip"
                  class="hidden popper-tooltip"
                >
                  {{ $t('messages_nl.letter.content.blurTooltip') }}
                </span>

                <!-- Company Postcode & City-->
                <p>{{ $t('messages_nl.label.companyPostcode') + ' ' + $t('messages_nl.label.companyCity')}}</p>
              </template>

              <br />
              <br />

              <!-- location -->
              <adivare-preview-element
                class="mb-2"
                property="customerCity"
                focus-override="customerPostcode"
                :add-margin="false"
              />, {{ todayAsString }}

              <br />

              <!-- topic/subject -->
              <p>
                {{ $t('messages_nl.letter.concernWithTopic', {
                    'topic': getCancellationProperty('subscriptionName', null) || (cancellationSelected ? $t('messages_nl.label.cancellation.service').toLowerCase() : '')
                }) }}
              </p>

              <br />
              <br />

              <!-- salutation -->
              <p>{{ $t('messages_nl.letter.salutation') }}</p>

              <br />

              <!-- text: cancellation declaration -->
              <p>
                {{ $t('messages_nl.letter.content.cancelDeclaration.announcement', {
                  'subscriptionType': getCancellationProperty('subscriptionTypeName', $t('messages_nl.global._defaultSubscriptionType')),
                  'prefix': getCancellationProperty('subscriptionTypeNamePrefix', $t('messages_nl.letter.content._my')),
                }) }}
                <i18n-t keypath="messages_nl.letter.content.cancelDeclaration.request" scope="global">
                  <template v-slot:subscriptionType>
                    {{ getCancellationProperty('subscriptionTypeName', $t('messages_nl.global._defaultSubscriptionType')) }}
                  </template>
                  <template v-slot:prefix>
                    {{ getCancellationProperty('subscriptionTypeNamePrefix', $t('messages_nl.letter.content._my')) }}
                  </template>
                  <template v-slot:dateText>
                    <template v-if="hasCancellationDate">
                      <adivare-preview-element
                        property="opzegdatum"
                        preview-text-override="opzegdatum"
                        :add-margin=false
                      />
                    </template>
                    <template v-else>
                      {{ $t('messages_nl.letter.content.cancelNow') }}
                    </template>
                  </template>
                </i18n-t>
              </p>

              <br />

              <!-- text: custom overview intro -->
              <p>{{ $t('messages_nl.letter.content.overviewCustomer') }}</p>

              <br />

              <!-- table: customer overview list -->
              <table class="ml-4">
                <tbody>
                  <tr>
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.label.customerFirstName') }}:</strong></td>
                    <td class="align-top break-all">
                      <adivare-preview-element
                        property="customerFirstName"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.label.customerLastName') }}:</strong></td>
                    <td class="align-top break-all">
                      <adivare-preview-element
                        property="customerLastName"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.label.customerEmail') }}:</strong></td>
                    <td class="align-top break-all">
                      <adivare-preview-element
                        property="customerEmail"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.label.customerAddress') }}:</strong></td>
                    <td class="align-top break-all flex gap-x-1.5">
                      <adivare-preview-element
                        property="customerStreet"
                        focus-override="customerPostcode"
                      />
                      <div>
                        <adivare-preview-element
                          property="customerHouseNumber"
                        />
                        <adivare-preview-element
                          property="customerHouseNumberExtension"
                          :show-placeholder="false"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.label.customerPostcode') }}:</strong></td>
                    <td class="align-top break-all">
                      <adivare-preview-element
                        property="customerPostcode"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.label.customerCity') }}:</strong></td>
                    <td class="align-top break-all">
                      <adivare-preview-element
                        property="customerCity"
                        focus-override="customerPostcode"
                      />
                    </td>
                  </tr>
                  <tr v-if="!cancellationSelected">
                    <td class="pr-2 align-top"><strong>{{ $t('messages_nl.letter.content.subscription') }}:</strong></td>
                    <td class="align-top">{{ `${$t('messages_nl.label.cancellation.service')} ${$t('messages_nl.letter.content.cancel._cancel')}` }}</td>
                  </tr>
                  <tr
                    v-for="(additionalField, name) in additionalFieldsWithoutCancellationDate"
                    :key="name"
                   >
                    <td class="pr-2 align-top"><strong>{{ additionalField.name }}:</strong></td>
                    <td class="align-top break-all">
                      <adivare-preview-element
                        :property="additionalField.slug"
                        :preview-text-override="additionalField.name"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <br />

              <!-- text: additional request -->
              <p>
                {{ $t('messages_nl.letter.content.additionalRequest.stopIncasso') }}
                <template v-if="rightToResist">
                  {{ $t('messages_nl.letter.content.additionalRequest.stopAdvertisement') }}
                </template>
              </p>

              <br />

              <!-- text: confirmation request -->
              <p v-if="null !== email && '' !== email">
                {{ $t('messages_nl.letter.content.confirmationRequest.base') }} {{ $t('messages_nl.letter.content.confirmationRequest.mailAndPost', {'emailCustomer': email}) }}
              </p>
              <p v-else>
                {{ $t('messages_nl.letter.content.confirmationRequest.base') }} {{ $t('messages_nl.letter.content.confirmationRequest.postOnly') }}
              </p>

              <br />
              <br />

              <!-- closing remark -->
              <p>{{ $t('messages_nl.partials.letter.regards') }}</p>

              <br />

              <!-- signature -->
              <adivare-signature
                @click="toggleSignatureModal"
              />

              <!-- flash message -->
              <div
                v-if="showInvalidSignatureError"
                class="!mt-4 hidden md:block"
              >
                <adivare-alert
                  type="danger"
                  size="xs"
                  show-icon
                >
                  {{ $t('messages_nl.label.cancellation.userLetter.signature.desktopError') }}
                </adivare-alert>
              </div>
              <br />

              <!-- name -->
              <p class="flex gap-x-1.5">
                <adivare-preview-element
                  property="customerFirstName"
                />
                <adivare-preview-element
                  property="customerLastName"
                />
              </p>
            </slot>
          </div>

          <!-- legenda -->
          <p class="pb-10 md:pb-4 pt-4 px-6 text-fontcolor-light-grey text-sm bg-white border-t-1 rounded-b-lg border-box-against-white-background">{{ $t('messages_nl.partials.letter.required') }}</p>
        </template>
      </adivare-collapse>
    </div>

    <!-- signature modal -->
    <adivare-signature-modal
      :active="signatureModalActive"
      ref="signatureModal"
      @close-modal="toggleSignatureModal"
      target="body"
    />
  </div>
</template>

<script>
import AdivareSignature from "./signature.vue";
import AdivareSignatureModal from "./signature-modal";
import AdivarePreviewElement from "./partials/preview-element.vue";
import AdivareGuaranteedModal from "./partials/guaranteed-modal.vue";
import AdivareSweetAlertModal from "/assets/vue/components/sweet-alert-modal.vue";
import AdivareCollapse from "/assets/vue/components/collapse";
import AdivareAlert from '../../../components/alert.vue';
import { MODE_HAND_DRAWN } from '../../../signature-utils';
import { createPopper } from '@popperjs/core';
import { mapGetters } from 'vuex';

export default {
  name: 'adivare-letter-preview',
  components: {
    AdivareSignature,
    AdivarePreviewElement,
    AdivareSignatureModal,
    AdivareSweetAlertModal,
    AdivareGuaranteedModal,
    AdivareCollapse,
    AdivareAlert,
  },
  data() {
    return {
      popper: {
        button: null,
        tooltip: null,
      },
      signatureModalActive: false,
      expandCollapsible: false,
    }
  },
  computed: {
    ...mapGetters([
      'generic',
      'isGuaranteed',
      'getActiveCancellation',
      'hasValidSignature',
      'showErrors',
    ]),
    showInvalidSignatureError() {
      const showError = this.showErrors && false === this.hasValidSignature;
      if (true === showError) {
        this.expandCollapsible = true;
      }

      return showError;
    },
    rightToResist() {
      return this.$store.getters.getFormField('customerRightToResist');
    },
    email() {
      return this.$store.getters.getFormField('customerEmail');
    },
    addressIsBelgian() {
      const country = this.$store.getters.getCountry;
      return country && country.belgium;
    },
    cancellationSelected() {
      return null !== this.getActiveCancellation;
    },
    additionalFields() {
      try {
        return this.getActiveCancellation.additionalFields ?? [];
      } catch (TypeError) {
        // Cancellation has not yet been set
        return [];
      }
    },
    additionalFieldsWithoutCancellationDate() {
      //shallow copy is safe in this case
      const copy = [];
      this.additionalFields.forEach(field => {
        if ('opzegdatum' !== field.slug) {
          copy.push(field);
        }
      });

      return copy;
    },
    hasCancellationDate() {
      const hasField = true;
      if (true === hasField) {
        const date = this.$store.getters.cancellationDate;

        return date instanceof Date && date.getDate() !== (new Date).getDate();
      }
    },
    todayAsString() {
      return (new Date()).toLocaleDateString(this.$i18n.locale);
    },
  },
  methods: {
    setPopperElements() {
      this.popper = {
        button: this.popper.button || this.$refs['data-popper-button'],
        tooltip: this.popper.tooltip || this.$refs['data-popper-tooltip']
      };
    },
    showPopper() {
      this.setPopperElements();
      this.popper.tooltip.classList.remove('hidden');
      createPopper(
        this.popper.button,
        this.popper.tooltip,
        {
          placement: 'top',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        }
      ).update();
    },
    hidePopper() {
      this.setPopperElements();
      this.popper.tooltip.classList.add('hidden');
    },
    getPreviewText(property, fallbackTranslationKey) {
      return this.$store.getters.getFormField(property) ?? this.$t(`messages_nl.label.${fallbackTranslationKey}`);
    },
    toggleSignatureModal() {
      this.$store.commit('signatureMode', MODE_HAND_DRAWN);
      this.signatureModalActive = !this.signatureModalActive;
    },
    focusProperty(propertyName) {
      this.$store.dispatch('focusInputRef', propertyName);
    },
    getCancellationProperty(propertyName, defaultValue = '') {
      try {
        return this.getActiveCancellation[propertyName];
      } catch (TypeError) {
        return defaultValue;
      }
    },
    hasAdditionalField(fieldName) {
      let foundField = false;
      this.additionalFields.forEach(field => foundField += Number(fieldName === field.slug));
      return Boolean(foundField);
    },
  },
}
</script>
